import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { navLinks } from '../../data/mock';
import AuthModal from '../auth/AuthModal';
import authService from '../../services/auth';

const LOGO_EMBLEM = 'https://customer-assets.emergentagent.com/job_3118c9a4-a94d-4f60-9004-669a5548876d/artifacts/9jvjmy2x_GWINS_Symbol_Metallic_2000px.png';

const Header = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check for logged in user
    const checkUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        if (user) {
          setCurrentUser(user);
          authService.storeUser(user);
        } else {
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setCurrentUser(storedUser);
          }
        }
      } catch (error) {
        console.error('Error checking user:', error);
      }
    };
    checkUser();
  }, []);

  const handleLogout = async () => {
    await authService.logout();
    setCurrentUser(null);
  };

  const handleAuthSuccess = (user) => {
    setCurrentUser(user);
    setShowAuthModal(false);
    // Redirect to community
    navigate('/community');
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0a0b0c]/95 backdrop-blur-md border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-28">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img
                src={LOGO_EMBLEM}
                alt="GWINS Gear"
                className={`transition-all duration-300 ${isScrolled ? 'h-16' : 'h-24'}`}
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-white/80 hover:text-[#00d4aa] transition-colors font-medium text-sm uppercase tracking-wider"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="text-white/80 hover:text-[#00d4aa] hover:bg-white/5"
              >
                <ShoppingCart className="h-5 w-5" />
              </Button>
              
              {currentUser ? (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => navigate('/community')}
                    variant="ghost"
                    className="text-white/80 hover:text-[#00d4aa]"
                  >
                    {currentUser.picture && (
                      <img 
                        src={currentUser.picture} 
                        alt="" 
                        className="w-6 h-6 rounded-full mr-2"
                      />
                    )}
                    {currentUser.name}
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    size="icon"
                    className="text-white/60 hover:text-white"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-[#00d4aa] text-[#0a0b0c] hover:bg-[#33e0bb] font-semibold rounded-full px-6"
                  data-testid="header-signin-btn"
                >
                  <User className="h-4 w-4 mr-2" />
                  Sign In
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-[#131416] border-t border-white/5 py-4">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-white/80 hover:text-[#00d4aa] px-4 py-3 font-medium text-sm uppercase tracking-wider"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
                <div className="border-t border-white/5 mt-2 pt-4 px-4 flex gap-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/80 hover:text-[#00d4aa]"
                  >
                    <ShoppingCart className="h-5 w-5" />
                  </Button>
                  
                  {currentUser ? (
                    <Button 
                      onClick={() => { navigate('/community'); setIsMobileMenuOpen(false); }}
                      className="bg-[#00d4aa] text-[#0a0b0c] hover:bg-[#33e0bb] font-semibold rounded-full flex-1"
                    >
                      <User className="h-4 w-4 mr-2" />
                      My Account
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => { setShowAuthModal(true); setIsMobileMenuOpen(false); }}
                      className="bg-[#00d4aa] text-[#0a0b0c] hover:bg-[#33e0bb] font-semibold rounded-full flex-1"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Sign In
                    </Button>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        redirectPath="/community"
      />
    </>
  );
};

export default Header;
